import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Product } from "@/types/product";
import { Pencil } from "lucide-react";

interface EditProductDialogProps {
  product: Product;
  onUpdate: (product: Product) => void;
}

export function EditProductDialog({ product, onUpdate }: EditProductDialogProps) {
  const [open, setOpen] = useState(false);
  const [installmentError, setInstallmentError] = useState("");
  const [formData, setFormData] = useState({
    deviceName: product.deviceName,
    deviceDescription: product.deviceDescription || "",
    salePrice: product.salePrice.toString(),
    monthlyInstallment: product.monthlyInstallment.toString(),
    currency: product.currency,
    date: product.date,
    notes: product.notes,
  });

  useEffect(() => {
    setFormData({
      deviceName: product.deviceName,
      deviceDescription: product.deviceDescription || "",
      salePrice: product.salePrice.toString(),
      monthlyInstallment: product.monthlyInstallment.toString(),
      currency: product.currency,
      date: product.date,
      notes: product.notes,
    });
    setInstallmentError("");
  }, [product]);

  const handleInstallmentChange = (value: string) => {
    setFormData({ ...formData, monthlyInstallment: value });
    const installment = parseFloat(value) || 0;
    const salePrice = parseFloat(formData.salePrice) || 0;
    
    if (installment > salePrice) {
      setInstallmentError("قیستی مانگانە نابێت زیاتر بێت لە نرخی فرۆشتن");
    } else {
      setInstallmentError("");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const salePrice = parseFloat(formData.salePrice);
    const monthlyInstallment = parseFloat(formData.monthlyInstallment);
    
    if (monthlyInstallment > salePrice) {
      setInstallmentError("قیستی مانگانە نابێت زیاتر بێت لە نرخی فرۆشتن");
      return;
    }
    
    onUpdate({
      ...product,
      deviceName: formData.deviceName,
      deviceDescription: formData.deviceDescription,
      salePrice,
      monthlyInstallment,
      currency: formData.currency,
      remainingAmount: salePrice - product.paidAmount,
      date: formData.date,
      notes: formData.notes,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) setInstallmentError("");
    }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-500/10 md:flex hidden">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogTrigger asChild>
        <Button size="sm" className="flex-1 bg-blue-500 text-white hover:bg-blue-600 md:hidden flex">
          <Pencil className="h-4 w-4 ml-2" />
          دەستکاریکردن
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle className="text-right">دەستکاری کاڵا</DialogTitle>
          <DialogDescription className="text-right">زانیاریەکانی کاڵا دەستکاری بکە</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deviceName">ناوی ئامێر *</Label>
              <Input
                id="deviceName"
                value={formData.deviceName}
                onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                required
                placeholder="ناوی ئامێر"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deviceDescription">وەسفی ئامێر</Label>
              <Input
                id="deviceDescription"
                value={formData.deviceDescription}
                onChange={(e) => setFormData({ ...formData, deviceDescription: e.target.value })}
                placeholder="وەسفی ئامێر"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">دراو</Label>
            <Input
              id="currency"
              value={formData.currency === "IQD" ? "دینار (IQD)" : "دۆلار (USD)"}
              disabled
              className="bg-muted"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="salePrice">نرخی فرۆشتن</Label>
              <Input
                id="salePrice"
                type="number"
                value={formData.salePrice}
                disabled
                className="bg-muted"
                dir="ltr"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthlyInstallment">قیستی مانگانە *</Label>
              <Input
                id="monthlyInstallment"
                type="number"
                value={formData.monthlyInstallment}
                onChange={(e) => handleInstallmentChange(e.target.value)}
                required
                placeholder="0"
                dir="ltr"
                className={installmentError ? "border-destructive" : ""}
              />
              {installmentError && (
                <p className="text-sm text-destructive">{installmentError}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="date">بەروار</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              disabled
              className="bg-muted"
              dir="ltr"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">تێبینی</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="تێبینی..."
              rows={2}
            />
          </div>
          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              پاشگەزبوونەوە
            </Button>
            <Button type="submit" disabled={!!installmentError}>پاشەکەوتکردن</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
